import React from 'react';
import styled from 'styled-components';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import ListItemButton from '@mui/material/ListItemButton';

import { Segment } from 'ff/debrief/models';
import { formatDuration, getSegmentId } from 'ff/debrief/utils';
import SegmentScore from 'ff/debrief/components/segments/SegmentScore';

interface Props {
    label?: string;
    category?: string;
    startOffsetInSeconds?: number;
    durationInSeconds?: number;
    color?: string;
    distance?: string;
    score?: number;
    level?: keyof typeof levelMargins;
    currentSegmentId: string;
    childSegments: Segment[];
    withBorders?: boolean;
    withTopBorders?: boolean;
    onClick?: (segment: Partial<Segment>) => void;
}

const levelMargins = {
    0: '0',
    1: '8px',
    2: '24px',
    3: '40px',
    4: '56px',
    5: '72px',
};

const SegmentItem = (props: React.PropsWithChildren<Props>) => {
    const { onClick, children, ...segment } = props;
    const isActive = getSegmentId(segment) === props.currentSegmentId;

    const title = (segment.label &&
        (segment.label.toLowerCase().includes('airborne')
            ? segment.distance
            : segment.label)) || <Skeleton width={200} />;
    const category =
        (segment.label &&
            (segment.label.toLowerCase().includes('airborne')
                ? 'Airborne'
                : segment.category)) ||
        '';
    const timestamp =
        segment.startOffsetInSeconds !== undefined ? (
            formatDuration(props.startOffsetInSeconds)
        ) : (
            <Skeleton width={40} />
        );

    const handleClick = () => {
        props.onClick(segment);
    };

    const margin = levelMargins[props.level];

    return (
        <Thing
            data-testid="thing-container"
            withBorder={props.withBorders}
            mb={props.withBorders || props.level === 0 ? 1 : 0}
        >
            <Container
                selected={isActive}
                onClick={handleClick}
                withTopBorder={props.withTopBorders}
            >
                <Content marginLeft={margin} gap={1}>
                    <Stack flexGrow={1}>
                        <Category>{category}</Category>
                        <Title
                            fontWeight={
                                props.childSegments.length > 0 ? 700 : 400
                            }
                        >
                            {title}
                        </Title>
                    </Stack>
                    <Stack width="48px">
                        <TimeStamp>{timestamp}</TimeStamp>
                        {props.distance && (
                            <Distance>{props.distance}</Distance>
                        )}
                    </Stack>
                    {typeof segment.score === 'number' ? (
                        <SegmentScore score={segment.score} />
                    ) : (
                        <Box width={27} />
                    )}
                </Content>
            </Container>
            {props.childSegments.map(({ children, ...s }) => (
                <SegmentItem
                    key={s.label + s.startOffsetInSeconds}
                    onClick={props.onClick}
                    currentSegmentId={props.currentSegmentId}
                    childSegments={children}
                    level={
                        (children.length > 0
                            ? props.level + 1
                            : props.level) as keyof typeof levelMargins
                    }
                    withTopBorders
                    {...s}
                />
            ))}
        </Thing>
    );
};

const Thing = styled(Box)<{ withBorder?: boolean }>`
    border-radius: 8px;
    ${props =>
        props.withBorder ? 'border: 1px solid rgba(0, 0, 0, 0.30);' : ''}
`;

const Container = styled(ListItemButton)<{ withTopBorder?: boolean }>`
    position: relative;
    display: flex;
    padding: 0 8px;
    flex-direction: row;
    align-items: stretch;
    ${props =>
        props.withTopBorder ? 'border-top: 1px solid rgba(0, 0, 0, 0.1);' : ''}
`;

const Content = styled(Box)`
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px;
`;

const Category = styled(Typography)`
    color: #646468;
    font-size: 11px;
    font-weight: 400;
`;

const Title = styled(Typography)`
    color: #1c1c1e;
    font-size: 13px;
    line-height: 17px;
`;

const TimeStamp = styled(Typography)`
    color: #697d90;
    font-size: 11px;
    font-weight: 400;
`;

const Distance = styled(Typography)`
    color: #697d90;
    font-size: 11px;
    font-weight: 700;
`;

export default SegmentItem;
